import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Mail, CalendarDays, Globe, Phone } from "lucide-react";

const GOLD = "#D4AF37", RED = "#E0141C", CARBON = "#070707";
// 3.75in × 2.25in (3.5×2 trim + 0.125in bleed) at 150 css px/in
const W = 562.5, H = 337.5, BLEED = 18.75;
const HEAD = "'Anton', Impact, 'Arial Narrow', sans-serif";
const MARK = "'Permanent Marker', cursive";

const rng = (seed) => () => { seed = (seed * 9301 + 49297) % 233280; return seed / 233280; };

// Dry-brush red slash (angular stroke with bristle streaks + rough edges) — the reference's signature.
const Slash = ({ seed = 3, w = 420, h = 120, color = RED, style, opacity = 0.96 }) => {
  const r = rng(seed);
  const streaks = Array.from({ length: 14 }, () => ({ y: 6 + r() * 88, x: r() * 30, len: 40 + r() * 60, t: 0.6 + r() * 2.2 }));
  const fid = `sl${seed}`;
  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="none" width={w} height={h} style={{ position: "absolute", opacity, ...style }} aria-hidden>
      <defs>
        <filter id={fid} x="-20%" y="-40%" width="140%" height="180%">
          <feTurbulence type="turbulence" baseFrequency="0.06 0.25" numOctaves="2" seed={seed} result="n" />
          <feDisplacementMap in="SourceGraphic" in2="n" scale="9" xChannelSelector="R" yChannelSelector="G" />
        </filter>
      </defs>
      <g filter={`url(#${fid})`} fill={color}>
        <polygon points="0,38 100,10 100,62 2,86" />
        <polygon points="8,30 96,4 98,16 10,44" opacity="0.7" />
        {streaks.map((s, i) => <rect key={i} x={s.x} y={s.y} width={s.len} height={s.t} opacity="0.55" />)}
        {streaks.slice(0, 7).map((s, i) => <rect key={`b${i}`} x={100 - s.len - s.x} y={100 - s.y} width={s.len} height={s.t} opacity="0.5" />)}
      </g>
    </svg>
  );
};

const Grit = () => (
  <>
    <svg className="absolute inset-0 w-full h-full" style={{ mixBlendMode: "overlay", opacity: 0.5 }} aria-hidden>
      <filter id="grit"><feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" seed="3" /><feColorMatrix values="0 0 0 0 0.5  0 0 0 0 0.5  0 0 0 0 0.5  0 0 0 0.9 0" /></filter>
      <rect width="100%" height="100%" filter="url(#grit)" />
    </svg>
    <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 50% 50%, transparent 58%, rgba(0,0,0,0.55) 100%)" }} />
  </>
);

// Red arrow banner (nextosoccer.com ribbon from the reference).
const Ribbon = ({ children, style, w = 190 }) => (
  <div className="absolute flex items-center" style={{ width: w, height: 22, ...style }}>
    <div className="absolute inset-0" style={{ background: `linear-gradient(90deg, ${RED}, #b30d13)`, clipPath: "polygon(0 0, 92% 0, 100% 50%, 92% 100%, 0 100%)", boxShadow: `0 6px 18px rgba(224,20,28,0.45)` }} />
    <span className="relative pl-3 font-mono text-[7.5px] font-bold tracking-[0.28em] uppercase text-white whitespace-nowrap">{children}</span>
  </div>
);

const Frame = ({ children, testid }) => (
  <div data-testid={testid} className="relative overflow-hidden text-white" style={{ width: W, height: H, background: CARBON, fontFamily: "Montserrat, sans-serif" }}>
    {children}
    <Grit />
  </div>
);

const Heading = ({ children, size, color = "#fff", style }) => (
  <div style={{ fontFamily: HEAD, fontSize: size, lineHeight: 0.9, textTransform: "uppercase", letterSpacing: "0.005em", color, transform: "skewX(-6deg) rotate(-3deg)", textShadow: "0 4px 0 rgba(0,0,0,0.75), 0 0 20px rgba(0,0,0,0.7)", ...style }}>{children}</div>
);

const Duotone = ({ src, style, pos = "50% 10%", clip, flip = false }) => (
  <div className="absolute overflow-hidden" style={{ ...style, clipPath: clip }}>
    <img src={src} alt="" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: pos, filter: "contrast(1.3) saturate(1.1)", transform: flip ? "scaleX(-1)" : undefined }} />
    <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(7,7,7,0.25) 0%, transparent 35%, transparent 60%, rgba(7,7,7,0.92) 100%)" }} />
  </div>
);

const FOIL = "linear-gradient(100deg, #7a5a10 0%, #D4AF37 22%, #FFF3C4 48%, #D4AF37 68%, #8a6a14 100%)";
const GoldText = ({ children, size, style }) => (
  <div style={{ fontFamily: HEAD, fontSize: size, lineHeight: 0.9, textTransform: "uppercase", letterSpacing: "0.01em", backgroundImage: FOIL, WebkitBackgroundClip: "text", color: "transparent", filter: "drop-shadow(0 3px 0 rgba(0,0,0,0.85)) drop-shadow(0 0 14px rgba(212,175,55,0.35))", ...style }}>{children}</div>
);

// Stealth Elite base: matte carbon weave + diagonal stealth stripes + ghosted engine telemetry.
const telemetry = (() => { const r = rng(11); return Array.from({ length: 26 }, (_, i) => `${(r() * 180).toFixed(1).padStart(5, "0")}°  X${(r() * 999).toFixed(0).padStart(3, "0")}.${(r() * 99).toFixed(0).padStart(2, "0")}  Y${(r() * 999).toFixed(0).padStart(3, "0")}  STAB ${(80 + r() * 19).toFixed(0)}%  HIP ${(60 + r() * 39).toFixed(0)}%  T+${(i * 0.033).toFixed(3)}s  //  NXO-ENGINE-${(1000 + r() * 8999).toFixed(0)}`); })();
const Stealth = () => (
  <>
    <div className="absolute inset-0" style={{ background: `radial-gradient(60% 70% at 50% 45%, #141414, #070707 80%)` }} />
    <div className="absolute inset-0" style={{ opacity: 0.55, backgroundImage: "linear-gradient(45deg, rgba(255,255,255,0.06) 25%, transparent 25%, transparent 50%, rgba(255,255,255,0.06) 50%, rgba(255,255,255,0.06) 75%, transparent 75%), linear-gradient(-45deg, rgba(0,0,0,0.45) 25%, transparent 25%, transparent 50%, rgba(0,0,0,0.45) 50%, rgba(0,0,0,0.45) 75%, transparent 75%)", backgroundSize: "6px 6px" }} />
    <div className="absolute inset-0" style={{ backgroundImage: "repeating-linear-gradient(-32deg, transparent 0 22px, rgba(255,255,255,0.028) 22px 34px)" }} />
    <div className="absolute inset-0 overflow-hidden font-mono leading-[1.55]" style={{ fontSize: 5.2, color: "rgba(212,175,55,0.16)", letterSpacing: "0.12em", padding: "6px 10px", whiteSpace: "nowrap", transform: "rotate(-6deg) scale(1.15)", mixBlendMode: "screen" }}>
      {telemetry.map((l, i) => <div key={i} style={{ opacity: i % 3 === 0 ? 1 : 0.6 }}>{l}</div>)}
    </div>
  </>
);

// Soft-touch crimson paint splatter.
const Splatter = ({ seed = 21, size = 160, style, opacity = 0.55 }) => {
  const r = rng(seed);
  const blobs = Array.from({ length: 16 }, () => ({ x: 30 + r() * 40, y: 30 + r() * 40, rx: 2 + r() * 12, ry: 2 + r() * 9, o: 0.35 + r() * 0.65 }));
  const id = `sp${seed}`;
  return (
    <svg viewBox="0 0 100 100" width={size} height={size} style={{ position: "absolute", opacity, mixBlendMode: "screen", ...style }} aria-hidden>
      <defs><filter id={id} x="-30%" y="-30%" width="160%" height="160%"><feTurbulence type="turbulence" baseFrequency="0.09" numOctaves="3" seed={seed} result="n" /><feDisplacementMap in="SourceGraphic" in2="n" scale="22" /><feGaussianBlur stdDeviation="0.35" /></filter></defs>
      <g filter={`url(#${id})`} fill={RED}>
        <ellipse cx="50" cy="50" rx="18" ry="13" opacity="0.9" />
        {blobs.map((b, i) => <ellipse key={i} cx={b.x} cy={b.y} rx={b.rx} ry={b.ry} opacity={b.o} />)}
      </g>
    </svg>
  );
};

const Portrait = ({ src, style, pos, zoom = 2.1, flip = false }) => (
  <div className="absolute overflow-hidden" style={style}>
    <img src={src} alt="" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: pos, transform: `scale(${zoom})${flip ? " scaleX(-1)" : ""}`, transformOrigin: pos, filter: "grayscale(0.75) contrast(1.55) brightness(0.92) saturate(1.2)" }} />
    <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(7,7,7,0.35) 0%, transparent 25%, transparent 55%, rgba(7,7,7,0.97) 100%)" }} />
    <div className="absolute inset-0" style={{ background: "radial-gradient(60% 50% at 50% 30%, transparent 55%, rgba(7,7,7,0.6) 100%)" }} />
  </div>
);

const Front = () => (
  <Frame testid="card-front">
    <Stealth />
    <Portrait src="/marketing/hero-male.jpg" pos="36% 11%" zoom={2.35} style={{ left: 0, top: 0, width: W / 2 + 4, height: H }} />
    <Portrait src="/marketing/hero-female.jpg" pos="42% 12%" zoom={2.5} style={{ left: W / 2 - 4, top: 0, width: W / 2 + 4, height: H }} />
    <div className="absolute" style={{ left: W / 2 - 1, top: 0, width: 2, height: H, background: `linear-gradient(180deg, transparent, ${GOLD}, transparent)`, opacity: 0.8 }} />
    <div className="absolute inset-0" style={{ background: "radial-gradient(70% 60% at 50% 100%, rgba(224,20,28,0.28), transparent 70%)" }} />
    <div className="absolute inset-0 overflow-hidden font-mono leading-[1.7]" style={{ fontSize: 5, color: "rgba(212,175,55,0.14)", letterSpacing: "0.14em", padding: "10px 12px", whiteSpace: "nowrap", transform: "rotate(-6deg) scale(1.15)", mixBlendMode: "screen" }}>
      {telemetry.map((l, i) => <div key={i} style={{ opacity: i % 2 ? 0.5 : 1 }}>{l}</div>)}
    </div>
    <Splatter seed={21} size={150} style={{ left: -30, bottom: 30 }} opacity={0.5} />
    <Splatter seed={33} size={120} style={{ right: 40, top: -20 }} opacity={0.42} />
    <Splatter seed={45} size={90} style={{ right: -20, bottom: 60 }} opacity={0.4} />
    <div className="absolute inset-x-0 bottom-0" style={{ height: 170, background: "linear-gradient(180deg, transparent, rgba(7,7,7,0.9) 60%)" }} />

    <img src="/brand/nexto-academy-horizontal.png" alt="The Next O Technical Academy" className="absolute object-contain" style={{ left: BLEED + 10, top: BLEED + 8, height: 52, width: "auto", filter: "drop-shadow(0 6px 14px rgba(0,0,0,0.95))" }} />
    <div className="absolute font-mono text-[6.5px] tracking-[0.4em] uppercase" style={{ right: BLEED + 12, top: BLEED + 12, color: GOLD, textShadow: "0 1px 4px #000" }}>Stealth Elite · Est. 2026</div>

    <div className="absolute inset-x-0 flex flex-col items-center text-center" style={{ bottom: BLEED + 34 }}>
      <GoldText size={30} style={{ letterSpacing: "0.12em" }}>The Next O</GoldText>
      <GoldText size={86} style={{ marginTop: -2, letterSpacing: "0.02em" }}>Standard</GoldText>
    </div>
    <div className="absolute font-mono text-[7px] tracking-[0.42em] uppercase text-white/85" style={{ left: BLEED + 14, bottom: BLEED + 14, textShadow: "0 1px 4px #000" }}>Train · Develop · Compete</div>
    <Ribbon style={{ right: BLEED + 12, bottom: BLEED + 12 }} w={170}>nextosoccer.com</Ribbon>
  </Frame>
);

const Plate = ({ children, style, size = 12 }) => (
  <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-black" style={{ border: `1.5px solid ${RED}`, boxShadow: "0 6px 16px rgba(0,0,0,0.7)", transform: "skewX(-8deg)", ...style }}>
    <span className="inline-flex items-center gap-2 font-bold text-white" style={{ transform: "skewX(8deg)", fontSize: size, letterSpacing: "0.02em" }}>{children}</span>
  </div>
);

const Contact = ({ icon: Icon, children, size = 11 }) => (
  <div className="flex items-center gap-2 font-mono font-bold text-white whitespace-nowrap" style={{ fontSize: size, letterSpacing: "0.04em", textShadow: "0 2px 6px rgba(0,0,0,0.9)" }}>
    <span className="inline-flex items-center justify-center rounded-sm" style={{ width: 16, height: 16, background: "rgba(212,175,55,0.14)", border: `1px solid ${GOLD}88` }}><Icon size={9} style={{ color: GOLD }} /></span>
    {children}
  </div>
);

const Back = () => (
  <Frame testid="card-back">
    <Stealth />
    <div className="absolute inset-0" style={{ background: "radial-gradient(260px 300px at 20% 60%, rgba(224,20,28,0.42), transparent 70%)" }} />
    <Splatter seed={57} size={140} style={{ left: 140, bottom: -30 }} opacity={0.35} />
    <Splatter seed={61} size={110} style={{ right: -20, top: -30 }} opacity={0.35} />

    <img src="/marketing/coach-cutout.png" alt="Coach Dwain Barker" className="absolute object-contain object-bottom" onError={(e) => { e.currentTarget.src = "/marketing/coach-barker.jpg"; e.currentTarget.style.objectFit = "cover"; e.currentTarget.style.maskImage = "linear-gradient(90deg, #000 70%, transparent), linear-gradient(180deg, #000 80%, transparent)"; }}
      style={{ left: -4, bottom: 0, width: 226, height: H - 8, filter: "contrast(1.15) saturate(1.05) drop-shadow(-4px 0 12px rgba(212,175,55,0.45)) drop-shadow(0 12px 22px rgba(0,0,0,0.85))" }} />
    <div className="absolute" style={{ left: 0, bottom: 0, width: 240, height: 70, background: "linear-gradient(180deg, transparent, rgba(7,7,7,0.9))" }} />

    <div className="absolute" style={{ left: 214, top: BLEED + 8, right: BLEED + 8 }}>
      <img src="/brand/nexto-academy-horizontal.png" alt="The Next O Technical Academy" className="object-contain" style={{ height: 44, width: "auto", filter: "drop-shadow(0 4px 10px rgba(0,0,0,0.9))", marginBottom: 6 }} />
      <GoldText size={44} style={{ letterSpacing: "0.02em" }}>Dwain Barker</GoldText>
      <GoldText size={19} style={{ marginTop: 3, letterSpacing: "0.14em" }}>Technical Director</GoldText>
      <div className="font-mono text-[6px] tracking-[0.35em] uppercase mt-1.5 text-white/60">Founder · The Next O Technical Academy</div>
      <div className="mt-3 flex flex-col gap-2">
        <Contact icon={Phone} size={13}>309.621.5704</Contact>
        <Contact icon={Mail} size={10}>d.barker@nextosoccer.com</Contact>
        <Contact icon={Globe} size={9}>nextosoccer.com</Contact>
        <Contact icon={CalendarDays} size={9}>nextosoccer.setmore.com</Contact>
      </div>
    </div>

    {/* The Access Key — QR centered in a metallic gold-bordered square */}
    <div className="absolute flex flex-col items-center" style={{ right: BLEED + 12, bottom: BLEED + 8 }}>
      <div className="relative flex items-center justify-center" style={{ width: 88, height: 88, padding: 3, background: FOIL, boxShadow: "0 0 0 1px #000, 0 0 22px rgba(212,175,55,0.45), 0 14px 28px rgba(0,0,0,0.85)" }}>
        <div className="absolute inset-[3px] bg-black" />
        <div className="relative bg-white p-1" style={{ width: 74, height: 74 }}>
          <img src="/marketing/official-qr.png" alt="QR" style={{ width: 66, height: 66, display: "block" }} />
        </div>
      </div>
      <div className="mt-1.5 font-mono text-[6px] tracking-[0.34em] uppercase" style={{ color: GOLD }}>Access Key · 7 Days Elite</div>
    </div>
  </Frame>
);

export default function PrintCard() {
  const { side } = useParams();
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Anton&family=Permanent+Marker&display=swap";
    document.head.appendChild(link);
    const t = setTimeout(() => document.fonts.ready.then(() => setReady(true)), 1200);
    return () => clearTimeout(t);
  }, []);
  return (
    <div className="min-h-screen bg-[#050505] flex items-start justify-center">
      <style>{`@page { size: 3.75in 2.25in; margin: 0; } html, body { background: #050505; } body { margin: 0; }`}</style>
      {ready && <span data-testid="card-ready" className="hidden" />}
      {side === "back" ? <Back /> : <Front />}
    </div>
  );
}

export { Slash, Grit, Ribbon, Heading, Duotone, Plate, RED as CARD_RED, HEAD, MARK };
